/**
 * Run `worker` over `items` with at most `limit` promises in flight at a time.
 *
 * Exists because several views fetch one note per id and a plain
 * `Promise.all(ids.map(...))` opens one XHR per note simultaneously — a 400-note
 * team, or a long watched list, saturates the browser's connection pool and
 * stalls every other request on the page. Results keep the input order.
 */
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;

  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor++;
      results[index] = await worker(items[index]);
    }
  });

  await Promise.all(runners);
  return results;
}

/**
 * Shared ceiling for per-note fetch fan-out. Six is what the team-notes view
 * settled on; keeping one number means the two views can't drift apart.
 */
export const NOTE_FETCH_CONCURRENCY = 6;
